<template>
  <Transition name="toast">
    <div v-if="visible" :class="['toast', `toast-${type}`, { 'toast-with-undo': hasUndo }]">
      <div class="toast-icon">
        <svg v-if="type === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <svg v-else-if="type === 'error'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <svg v-else-if="type === 'warning'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
      </div>
      <div class="toast-content">
        <div v-if="title" class="toast-title">{{ title }}</div>
        <div class="toast-message">{{ message }}</div>
      </div>
      <div v-if="hasUndo" class="toast-actions">
        <button class="toast-undo" @click="handleUndo">
          撤销<span v-if="remainingTime > 0"> ({{ remainingTime }}s)</span>
        </button>
      </div>
      <button v-else class="toast-close" aria-label="关闭" @click="close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';

const props = defineProps<{
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  undoId?: string; // 撤销操作的ID
}>();

const emit = defineEmits<{
  close: [id: string];
  undo: [undoId: string];
}>();

const visible = ref(false);
const remainingTime = ref(3);
let timer: ReturnType<typeof setTimeout> | null = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const hasUndo = computed(() => !!props.undoId);

onMounted(() => {
  // 触发进入动画
  visible.value = true;

  // 如果有撤销功能，启动倒计时
  if (hasUndo.value) {
    remainingTime.value = 3;
    countdownTimer = setInterval(() => {
      remainingTime.value--;
      if (remainingTime.value <= 0) {
        close();
      }
    }, 1000);
  }

  // 自动关闭
  if (props.duration && props.duration > 0) {
    timer = setTimeout(() => {
      close();
    }, props.duration);
  }
});

function close() {
  visible.value = false;
  // 等待退出动画完成后触发关闭事件
  setTimeout(() => {
    emit('close', props.id);
  }, 300);
}

function handleUndo() {
  emit('undo', props.undoId!);
  close();
}

// 清理定时器
onBeforeUnmount(() => {
  if (timer) {
    clearTimeout(timer);
  }
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
});
</script>

<style scoped>
.toast {
  position: fixed;
  top: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 500px;
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  z-index: 9999;
}

.toast-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 4px;
  color: var(--text-primary);
}

.toast-message {
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
  word-break: break-word;
}

.toast-actions {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  align-items: center;
}

.toast-undo {
  padding: 6px 12px;
  border: none;
  background: var(--color-primary);
  color: white;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.toast-undo:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.toast-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.toast-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

/* Toast 类型样式 */
.toast-success {
  border-left: 4px solid var(--success-text);
}

.toast-success .toast-icon {
  color: var(--success-text);
}

.toast-error {
  border-left: 4px solid var(--error-text);
}

.toast-error .toast-icon {
  color: var(--error-text);
}

.toast-warning {
  border-left: 4px solid var(--warning-text);
}

.toast-warning .toast-icon {
  color: var(--warning-text);
}

.toast-info {
  border-left: 4px solid var(--info-text);
}

.toast-info .toast-icon {
  color: var(--info-text);
}

/* 带撤销按钮的 Toast */
.toast-with-undo {
  min-width: 400px;
}

/* 过渡动画 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* 暗夜模式 */
:global(.dark) .toast {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

:global(.dark) .toast-title {
  color: var(--text-primary);
}

:global(.dark) .toast-message {
  color: var(--text-secondary);
}

:global(.dark) .toast-close {
  color: var(--text-tertiary);
}

:global(.dark) .toast-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

/* 移动端适配 */
@media (max-width: 640px) {
  .toast {
    left: 16px;
    right: 16px;
    min-width: 0;
    max-width: none;
  }

  .toast-with-undo {
    min-width: 0;
  }

  .toast-undo {
    font-size: 0.8125rem;
    padding: 5px 10px;
  }
}
</style>
